import { t } from 'i18next';
import type { I18nInfo, PtwValidationResult } from '@/api/generator';
import { ApiError, type KnownApiError } from './typeGuards';
import type { I18nKey } from '@/utils/typings';

/** Correctly-typed `t()` wrapper for an optional i18n key/parameters pair. */
export function translateI18nInfo(i18n?: I18nInfo): string | undefined {
  return i18n?.key
    ? (t(i18n.key as I18nKey, i18n.parameters as never) as unknown as string)
    : undefined;
}

/** The generic per-status message shown when the response carries no usable i18n key or
 * exception message of its own. */
function fallbackMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return t('Error.Category.BadRequest.message');
    case 401:
      return t('Error.Category.Unauthorized.message');
    case 403:
      return t('Error.Category.Forbidden.message');
    case 404:
      return t('Error.Category.NotFound.message');
    case 422:
      return `${t('Error.Category.SemanticError.title')}. ${t('Error.Category.SemanticError.message')}`;
    case 500:
    case 502:
    case 503:
    case 504:
      return t('Error.Category.ServerError.message');
    default:
      return t('Error.Category.Other.message');
  }
}

/** Extract localized message: i18n.key → message → a fallback chosen from the response's HTTP status */
export function extractErrorMessage(error: KnownApiError): string {
  const exception = error.error?.exception;

  if (!exception) return fallbackMessageForStatus(error.response.status);

  const translated = translateI18nInfo(exception.i18n);
  if (translated) return translated;

  if (exception.message) return exception.message;

  return fallbackMessageForStatus(error.response.status);
}

export function hasI18nInfo(error: KnownApiError): boolean {
  return !!error.error?.exception?.i18n?.key;
}

/**
 * `Ptw100BadRequestException` is the only exception schema declaring `validationErrors`; the
 * others set `additionalProperties: false`, so it can never reach the wire for them.
 */
export type MayHaveValidationErrors = Extract<KnownApiError, { name: typeof ApiError.badRequest }>;

export function extractValidationErrors(
  error: MayHaveValidationErrors,
): PtwValidationResult[] | undefined {
  return error.error?.exception?.validationErrors ?? undefined;
}
